// MF-574 — The closed list of colour palettes of the Boards. Maestro controls them (a school picks one, it does not
// invent colours); a Board picks one and, optionally, its primary colour. The keys are checked by the backend
// (BOARD_PALETTES in boards-policy.helper.js) — keep both lists the same.
import { createContext, useContext } from 'react'

// Blue ↔ orange scale (15 steps, dark orange → light → dark blue): for many series and for benchmarks (0–100).
export const BLUE_ORANGE_SCALE = [
  '#4D0507',
  '#6D2312',
  '#913D1A',
  '#B45B23',
  '#D77B30',
  '#F3A452',
  '#FDD08E',
  '#FFFFDE',
  '#BFCAEE',
  '#87A9D7',
  '#5389C1',
  '#2A69A7',
  '#224783',
  '#162F62',
  '#0E1841'
]

export const PALETTES = {
  // What we recommend: the blue as the primary colour, the orange to contrast with it.
  recommended: {
    label: 'Recommended (blue and orange)',
    colors: ['#2A69A7', '#F3A452', '#87A9D7', '#B45B23', '#162F62']
  },
  'blue-orange': { label: 'Blue–orange scale', colors: BLUE_ORANGE_SCALE },
  categorical: {
    label: 'Categorical blue–orange',
    colors: ['#B45B23', '#F3A452', '#87A9D7', '#2A69A7', '#162F62']
  },
  'okabe-ito': {
    label: 'Okabe–Ito (colour-blind safe)',
    colors: [
      '#0072B2',
      '#D55E00',
      '#009E73',
      '#E69F00',
      '#D77B30',
      '#CC79A7',
      '#56B4E9',
      '#E6C200',
      '#333333'
    ]
  },
  muted: {
    label: 'Muted',
    colors: ['#332288', '#882255', '#117733', '#AA4499', '#CC6677', '#999933', '#44AA99', '#DDCC77']
  },
  dark: {
    label: 'Dark',
    colors: ['#002B5B', '#8C510A', '#7B3294', '#637989', '#BF5B17', '#00441B', '#4A001F', '#888888']
  },
  neutral: { label: 'Neutral greys', colors: ['#333333', '#637989', '#98A3B3', '#C5CCD6', '#E3E7ED'] }
}
export const DEFAULT_PALETTE = 'recommended'

const lerp = (a, b, t) => Math.round(a + (b - a) * t)
const hex = (c) => [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16))
const toHex = (rgb) => `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`
const mix = (c1, c2, t) => toHex(hex(c1).map((v, i) => lerp(v, hex(c2)[i], t)))

/** A colour from the blue–orange scale for a value 0..1 (a benchmark: low = orange, high = blue). */
export const scaleColor = (t) => {
  const n = BLUE_ORANGE_SCALE.length - 1
  const p = Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0)) * n
  const i = Math.min(n - 1, Math.floor(p))
  return mix(BLUE_ORANGE_SCALE[i], BLUE_ORANGE_SCALE[i + 1], p - i)
}

/**
 * The colours of a tile: its own palette if it picked one, else the Board's. The primary colour (the tile's, else the
 * Board's) replaces the first colour. With more series than the palette has colours, the rest are spread over the
 * blue–orange scale (a gradient) instead of repeating.
 */
export const resolveColors = (settings = {}, tileColors = {}, count = 1) => {
  const key = tileColors?.palette || settings?.palette || DEFAULT_PALETTE
  const base = [...(PALETTES[key] || PALETTES[DEFAULT_PALETTE]).colors]
  const primary = tileColors?.primary || settings?.primary
  if (primary && key !== 'blue-orange') base[0] = primary
  if (count <= base.length) return base.slice(0, Math.max(count, 1))
  const extra = Array.from({ length: count - base.length }, (_, i) =>
    scaleColor((i + 1) / (count - base.length + 1))
  )
  return [...base, ...extra]
}

export const BoardStyleContext = createContext({
  palette: DEFAULT_PALETTE,
  primary: null,
  studentFirst: false
})
export const useBoardStyle = () => useContext(BoardStyleContext)

// A benchmark from 0 to 100: orange when low, through light orange and light blue, to blue when high.
const BENCHMARK_STOPS = ['#B45B23', '#F3A452', '#87A9D7', '#2A69A7', '#162F62']
export const benchmarkColor = (value, max = 100) => {
  const t = Math.min(1, Math.max(0, Number(value) / max))
  if (!Number.isFinite(t)) return BENCHMARK_STOPS[0]
  const p = t * (BENCHMARK_STOPS.length - 1)
  const i = Math.min(BENCHMARK_STOPS.length - 2, Math.floor(p))
  return mix(BENCHMARK_STOPS[i], BENCHMARK_STOPS[i + 1], p - i)
}
